import React, { useRef, useState } from 'react';
import { Link2, Plus, UploadCloud, FileVideo } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { api, formatApiError } from '../lib/api';
import { fieldCls, labelCls } from './member/ProfileForm';

const MAX_BYTES = 300 * 1024 * 1024;

export const AddSocialPostDialog = ({ open, onClose, onCreated }) => {
  const [tab, setTab] = useState('upload');
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const reset = () => { setUrl(''); setTitle(''); setFile(null); setProgress(0); };

  const submitLink = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post('/social-posts', { url, title });
      onCreated({ likes: 0, liked: false, favorited: false, ...data });
      toast.success('Videoen er publisert');
      reset(); onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const submitUpload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error('Vel ei videofil først');
    if (file.size > MAX_BYTES) return toast.error('Videoen kan maks vere 300 MB');
    setSaving(true);
    setProgress(0);
    try {
      const { data: init } = await api.post('/uploads/init', { filename: file.name, content_type: file.type || 'video/mp4', size: file.size });
      const chunkSize = init.chunk_size;
      const total = Math.ceil(file.size / chunkSize);
      for (let i = 0; i < total; i++) {
        const blob = file.slice(i * chunkSize, Math.min(file.size, (i + 1) * chunkSize));
        const form = new FormData();
        form.append('file', blob, `chunk-${i}`);
        await api.post(`/uploads/${init.upload_id}/chunk?index=${i}`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
        setProgress(Math.round(((i + 1) / total) * 95));
      }
      const { data } = await api.post(`/uploads/${init.upload_id}/complete`, { title });
      setProgress(100);
      onCreated(data);
      toast.success('Videoen er lasta opp og publisert');
      reset(); onClose();
    } catch (err) {
      toast.error(formatApiError(err, 'Opplastinga feila'));
    } finally {
      setSaving(false);
    }
  };

  const tabCls = (t) => `flex-1 py-2 rounded-full font-bold text-sm transition-colors ${tab === t ? 'bg-[#ff2e2e] text-black' : 'text-white/60 hover:text-white'}`;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent data-testid="social-add-dialog" className="max-w-md bg-[#0f0a17] border-white/10 text-white rounded-3xl [&>button]:text-white">
        <DialogTitle className="text-2xl font-bold" style={{ fontFamily: "'Oktah Round', sans-serif" }}>Legg til video</DialogTitle>
        <DialogDescription className="text-white/60 text-sm">Last opp ein video eller lim inn ei TikTok-/Instagram-lenke. Videoen blir vist under kartet på framsida.</DialogDescription>
        <div className="flex gap-1 p-1 rounded-full bg-white/5">
          <button type="button" data-testid="social-tab-upload" onClick={() => setTab('upload')} className={tabCls('upload')}>Last opp fil</button>
          <button type="button" data-testid="social-tab-link" onClick={() => setTab('link')} className={tabCls('link')}>Lenke</button>
        </div>

        {tab === 'upload' ? (
          <form onSubmit={submitUpload} className="space-y-4 mt-2" data-testid="social-upload-form">
            <button type="button" onClick={() => fileRef.current?.click()} data-testid="social-file-button" className="w-full rounded-2xl border-2 border-dashed border-white/15 hover:border-[#ff2e2e] p-6 flex flex-col items-center gap-2 text-white/70 transition-colors">
              {file ? <FileVideo size={28} className="text-[#ff2e2e]" /> : <UploadCloud size={28} className="text-[#ff2e2e]" />}
              <span className="text-sm font-semibold text-white">{file ? file.name : 'Vel videofil'}</span>
              <span className="text-xs text-white/40">{file ? `${(file.size / 1024 / 1024).toFixed(1)} MB` : 'MP4, MOV eller WEBM · maks 300 MB'}</span>
            </button>
            <input ref={fileRef} type="file" accept="video/mp4,video/quicktime,video/webm,video/x-m4v" className="hidden" data-testid="social-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            <div>
              <label className={labelCls}>Tittel (valfritt)</label>
              <input data-testid="social-upload-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="T.d. «Refuel 26 – highlights»" className={fieldCls} maxLength={120} />
            </div>
            {saving && (
              <div data-testid="social-upload-progress" className="h-2 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full bg-[#ff2e2e] transition-[width] duration-300" style={{ width: `${progress}%` }} />
              </div>
            )}
            <button type="submit" disabled={saving || !file} data-testid="social-upload-submit-button" className="w-full py-3.5 rounded-full bg-[#ff2e2e] text-black font-bold uppercase tracking-wide hover:bg-[#ff4d4d] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
              <UploadCloud size={18} /> {saving ? `Lastar opp… ${progress}%` : 'Last opp og publiser'}
            </button>
          </form>
        ) : (
          <form onSubmit={submitLink} className="space-y-4 mt-2" data-testid="social-link-form">
            <div>
              <label className={labelCls}>Lenke</label>
              <div className="relative">
                <Link2 size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                <input data-testid="social-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.tiktok.com/@refuel.oygarden/video/…" className={`${fieldCls} pl-12`} required />
              </div>
            </div>
            <div>
              <label className={labelCls}>Tittel (valfritt)</label>
              <input data-testid="social-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="T.d. «Refuel 26 – highlights»" className={fieldCls} maxLength={120} />
            </div>
            <button type="submit" disabled={saving} data-testid="social-submit-button" className="w-full py-3.5 rounded-full bg-[#ff2e2e] text-black font-bold uppercase tracking-wide hover:bg-[#ff4d4d] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
              <Plus size={18} /> {saving ? 'Publiserer…' : 'Publiser'}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
